/**
 * Fixed channel discriminator embedded in every envelope. Any `window.postMessage`
 * traffic that does not carry this exact `channel` value is treated as foreign and
 * silently ignored by both sides of the bridge.
 */
export const CHANNEL = "rc-web-components" as const;

/**
 * The single version number that couples the wire protocol and the SDK major
 * version. It is also the key used in the CDN `sdk.json` manifest to resolve the
 * content build to inject. There is intentionally no separate `sdk_version`.
 *
 * A future breaking change bumps this to `2` and ships `dist/sdk/v2/rc-sdk.js`; the
 * CDN publishes each build immutably under `releases/<version>/rc-sdk.js` and points
 * to it from `sdk.json` (see `.github/workflows/publish.yml`).
 */
export const DEFAULT_PROTOCOL_VERSION = 1;

/** Default timeout (ms) applied to {@link Bridge.request} calls. */
export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

/**
 * Application-level message the content side emits (fire-and-forget) whenever its
 * rendered content box changes size, so the host can size a `fit`/auto container to
 * the content. Payload is {@link ResizePayload}. Additive within protocol v1 — a
 * peer that does not understand it simply ignores it.
 */
export const RESIZE_MESSAGE = "resize" as const;

/**
 * Application-level message the host sends the content to declare which axes it is
 * sizing to the content (`fit`). On those axes the content suppresses its own
 * scrollbar: the host makes the frame exactly as tall/wide as the content, so a
 * scrollbar would only ever flash while the host catches up to a just-reported
 * size. Payload is {@link FitPayload}. Additive within protocol v1 — a peer that
 * does not understand it simply ignores it.
 */
export const FIT_MESSAGE = "fit" as const;

/**
 * Name of the native (iOS/Android) message channel used when the content side is
 * injected into a native WebView.
 *
 * - iOS   (WKWebView):  `window.webkit.messageHandlers.rcWebComponents.postMessage(json)`
 * - Android (WebView):  `window.rcWebComponents.postMessage(json)`
 *
 * This is distinct from {@link CHANNEL} (the envelope discriminator on the wire):
 * the wire format is identical across web and native; only the low-level delivery
 * differs.
 */
export const NATIVE_CHANNEL = "rcWebComponents" as const;

/**
 * Name of the global function the SDK installs on `window` for native -> JS
 * delivery. Native code calls `window.__rcWebComponentsReceive(data)` (via
 * `evaluateJavaScript` / `evaluateJavascript`) with a JSON string or an
 * already-parsed object.
 */
export const NATIVE_RECEIVE_GLOBAL = "__rcWebComponentsReceive" as const;

/** The set of transport framing kinds understood by the bridge. */
export const ENVELOPE_KINDS = [
  "connect",
  "init",
  "reject",
  "message",
  "request",
  "response",
  "error",
] as const;
